import { GTMAppStartupData, GTMEventData } from './types';
import { createElement, FC, useEffect } from 'react';
import { useGoogleTagManagerContainer } from 'apps/acquisition/packages/environment';
import {
  installGoogleTagManager,
  trackGTMEvent
} from 'packages/google-tag-manager';

interface Props {
  containerId: string;
}

/**
 * Tracks a Card Order Event
 *
 * @param email        - email address
 * @param referralCode - referral code of the account created
 * @param ddAnswer     - answer to the DD question
 */
export const trackCardOrder = (
  email: string,
  referralCode: string,
  ddAnswer?: string
) => {
  trackGTMEvent({
    event: 'nsCardOrder',
    nsCardOrder_email: email,
    nsCardOrder_referralCode: referralCode,
    ...(typeof ddAnswer === 'string' ? { nsCardOrder_ddAnswer: ddAnswer } : {})
  });
};

/**
 * Tracks an OAC Registration Event
 */
export const trackOACRegistration = () => {
  trackGTMEvent({
    event: 'nsRegistration'
  });
};

/**
 * Tracks an Event in GA
 *
 * @param category - GA Category
 * @param action   - GA Action
 * @param label    - GA Label
 */
export const trackGAEvent = (
  category: string,
  action: string,
  label: string
) => {
  trackGTMEvent({
    event: 'nsGAEvent',
    nsGAEvent_action: action,
    nsGAEvent_label: label,
    nsGAEvent_category: category
  });
};

/**
 * Dispatches the application startup with needed
 * data layer values
 *
 * @param data - Startup Data
 */
export const trackApplicationStartup = (data: GTMAppStartupData) => {
  const eventData: GTMEventData = {
    nsApp: 'online-acquisition',
    nsMode: data.mode.toLowerCase(),
    nsBrand: data.brand.toLowerCase(),
    nsCampaignContent: data.contextName.toLowerCase()
  };

  // Only append parameters that are present
  if (data.aid) {
    eventData['nsCampaignName'] = data.aid.toLowerCase();
  }

  if (data.siteId && eventData['nsCampaignName'] !== 'corporate') {
    eventData['nsCampaignSource'] = data.siteId.toLowerCase();
  }

  if (data.sub) {
    eventData['nsCampaignIdSub'] = data.sub.toLowerCase();
  }

  if (data.medium) {
    eventData['nsCampaignMedium'] = data.medium.toLowerCase();
  }

  trackGTMEvent(eventData);
};

/**
 * Component that installs Google Tag Manager
 * on to the page.
 * @param containerId - the GTM Container Id
 * @constructor
 */
const GoogleTagManagerParam: FC<Props> = ({ containerId }) => {
  useEffect(() => {
    installGoogleTagManager(containerId);
  }, [containerId]);

  // Nothing visual to add to the DOM
  return null;
};

export const GoogleTagManager: FC = () => {
  const containerId = useGoogleTagManagerContainer();
  return (
    (!!containerId && <GoogleTagManagerParam containerId={containerId} />) ||
    null
  );
};
